import { get } from "lodash"
import classNames from "classnames"
import { DEFAULT_METRIC_COLUMN_WIDTH } from "../constants"
import { VALUE_FORMATS } from "helpers/metrics/constants"
import { INTRADAY_PRICE_COLUMN_DATA_PATHS, contributorMetricLogicalDataPath } from "helpers/metrics"
import { getCurrencyScaleForValues } from "helpers/formatters/currency-scale"
import { buildScaledCurrencyLabel } from "helpers/metrics/currency-scale-label"

function getColumnType(format) {
  switch (format) {
    case VALUE_FORMATS.NUMBER:
    case VALUE_FORMATS.PERCENT:
    case VALUE_FORMATS.PERCENT_100:
    case VALUE_FORMATS.CURRENCY:
      return "number"
    case VALUE_FORMATS.STRING:
    default:
      return "string"
  }
}

function getMetricRawValue(metric, contributorValues) {
  return metric.dataGetter
    ? metric.dataGetter(contributorValues)
    : get(contributorValues, metric.dataPath)
}

function getColumnNumericValues(metric, sortedContributorsArray, currentLevelData) {
  return [
    ...(sortedContributorsArray || []).map(contributor => Number(getMetricRawValue(metric, contributor?.values))),
    Number(getMetricRawValue(metric, currentLevelData?.values)),
  ]
}

function isUnitPriceColumn(metric) {
  return INTRADAY_PRICE_COLUMN_DATA_PATHS.includes(contributorMetricLogicalDataPath(metric))
}

function createValueGetter(metric, columnType) {
  return (value, row) => {
    const contributor = row.contributor
    if (!contributor || !contributor.values) return null

    const rawValue = getMetricRawValue(metric, contributor.values)

    if (columnType === "number" && rawValue !== null && rawValue !== undefined) {
      return Number(rawValue)
    }

    return rawValue
  }
}

function createCellClassName(metric, metricCategories, groupEndFieldSet) {
  return () => {
    const categoryInfo = metricCategories?.[metric.category]
    const isLastInGroupFromCategory = categoryInfo ? metric.metricIndex === (categoryInfo.nbMetrics - 1) : false
    const isLastInGroupFromGroupingModel = groupEndFieldSet?.has?.(metric.id) || false
    const isLastInGroup = isLastInGroupFromGroupingModel || isLastInGroupFromCategory
    return classNames({
      "first-instrument": metric.isFirstInstrument,
      "group-end": isLastInGroup,
    })
  }
}

export const createMetricColumns = ({
  metricMetadata,
  metricCategories,
  groupEndFieldSet,
  sortMetricId,
  isAbsoluteSort,
  sortArrowDirection,
  onSortChange,
  data,
  sortedContributorsArray,
  MetricCellComponent,
  CustomHeaderComponent,
  savedDataGridState,
}) => {
  return metricMetadata.map((metric) => {
    const format = metric.format || metric.outputFormat
    const columnType = getColumnType(format)
    const isForcedScaleCurrencyColumn = format === VALUE_FORMATS.CURRENCY && !isUnitPriceColumn(metric)
    const columnScale = isForcedScaleCurrencyColumn
      ? getCurrencyScaleForValues(getColumnNumericValues(metric, sortedContributorsArray, data))
      : null
    const headerName = buildScaledCurrencyLabel(metric.label, columnScale)
    const metricForCell = columnScale ? { ...metric, forcedScale: columnScale } : metric

    const savedWidth = get(savedDataGridState, ["columns", "dimensions", metric.id, "width"])
    let columnWidth
    if (savedWidth && savedWidth > 0) {
      columnWidth = savedWidth
    } else if (metric.initialWidth) {
      columnWidth = metric.initialWidth
    }
    return {
      field: metric.id,
      headerName,
      currencyScale: columnScale,
      type: columnType,
      ...(columnWidth ? { width: columnWidth } : { minWidth: DEFAULT_METRIC_COLUMN_WIDTH }),
      align: "right",
      headerAlign: "right",
      sortable: false, // We handle sorting manually
      valueGetter: createValueGetter(metric, columnType),
      renderCell: (params) => (
        <MetricCellComponent
          params={params}
          metric={metricForCell}
        />
      ),
      renderHeader: (params) => (
        <CustomHeaderComponent
          field={metric.id}
          colDef={params.colDef}
          sortMetricId={sortMetricId}
          isAbsoluteSort={isAbsoluteSort}
          sortArrowDirection={sortArrowDirection}
          metricMetadata={metricMetadata}
          onSortChange={onSortChange}
          data={data}
        />
      ),
      cellClassName: createCellClassName(metric, metricCategories, groupEndFieldSet),
      headerClassName: classNames({
        "first-instrument": metric.isFirstInstrument,
        "group-start": metric.metricIndex === 0,
        "group-end": (groupEndFieldSet?.has?.(metric.id))
          || (((metricCategories?.[metric.category]?.nbMetrics ?? 0) > 0)
            ? metric.metricIndex === (metricCategories?.[metric.category]?.nbMetrics - 1)
            : false),
      }),
      pinnable: true,
      ...(metric.category && { groupId: metric.category }),
    }
  })
}
